import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ChevronRight,
  Tag,
  Plus,
  Edit,
  ToggleLeft,
  ToggleRight,
  History,
  X,
  Save,
  AlertCircle,
} from "lucide-react";
import {
  fetchCoupons,
  createCoupon,
  updateCoupon,
  toggleCouponActive,
  fetchCouponRedemptions,
} from "../api/CouponAPIs";

const emptyForm = {
  code: "",
  discountType: "Percentage",
  discountValue: "",
  isActive: true,
  startDateUtc: "",
  expiryDateUtc: "",
  maxUses: "",
  minimumProductQuantity: "",
  minimumProductsSubtotal: "",
  oneTimePerCustomer: false,
};

const formatDate = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleString("ar-EG");
};

const toInputDate = (value) => {
  if (!value) return "";
  return new Date(value).toISOString().slice(0, 16);
};

const parseOptionalInt = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const n = parseInt(value, 10);
  return Number.isNaN(n) ? null : n;
};

const parseOptionalDecimal = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const n = parseFloat(value);
  return Number.isNaN(n) ? null : n;
};

const buildPayload = (form) => ({
  code: form.code.trim(),
  discountType: form.discountType,
  discountValue: parseFloat(form.discountValue),
  isActive: form.isActive,
  startDateUtc: form.startDateUtc ? new Date(form.startDateUtc).toISOString() : null,
  expiryDateUtc: form.expiryDateUtc ? new Date(form.expiryDateUtc).toISOString() : null,
  maxUses: parseOptionalInt(form.maxUses),
  minimumProductQuantity: parseOptionalInt(form.minimumProductQuantity),
  minimumProductsSubtotal: parseOptionalDecimal(form.minimumProductsSubtotal),
  oneTimePerCustomer: form.oneTimePerCustomer,
});

const CouponsPage = () => {
  const navigate = useNavigate();
  const [coupons, setCoupons] = useState([]);
  const [formMode, setFormMode] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [historyCouponId, setHistoryCouponId] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["coupons"],
    queryFn: fetchCoupons,
    staleTime: Infinity,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (data) setCoupons(data);
  }, [data]);

  const openCreate = () => {
    setFormMode("create");
    setEditingId(null);
    setFormData(emptyForm);
  };

  const openEdit = (coupon) => {
    setFormMode("edit");
    setEditingId(coupon.couponId);
    setFormData({
      code: coupon.code,
      discountType: coupon.discountType,
      discountValue: String(coupon.discountValue),
      isActive: coupon.isActive,
      startDateUtc: toInputDate(coupon.startDateUtc),
      expiryDateUtc: toInputDate(coupon.expiryDateUtc),
      maxUses: coupon.maxUses != null ? String(coupon.maxUses) : "",
      minimumProductQuantity:
        coupon.minimumProductQuantity != null ? String(coupon.minimumProductQuantity) : "",
      minimumProductsSubtotal:
        coupon.minimumProductsSubtotal != null ? String(coupon.minimumProductsSubtotal) : "",
      oneTimePerCustomer: coupon.oneTimePerCustomer,
    });
  };

  const closeForm = () => {
    setFormMode(null);
    setEditingId(null);
    setFormData(emptyForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = buildPayload(formData);
      if (formMode === "create") {
        await createCoupon(payload);
        toast.success("تم إنشاء الكوبون بنجاح");
      } else {
        await updateCoupon(editingId, payload);
        toast.success("تم تحديث الكوبون بنجاح");
      }
      closeForm();
      refetch();
    } catch (err) {
      toast.error(err.message || "فشل حفظ الكوبون");
    }
  };

  const handleToggle = async (id) => {
    try {
      const result = await toggleCouponActive(id);
      setCoupons((prev) =>
        prev.map((c) => (c.couponId === id ? { ...c, isActive: result.isActive } : c))
      );
      toast.success(result.isActive ? "تم تفعيل الكوبون" : "تم إيقاف الكوبون");
    } catch {
      toast.error("فشل تغيير حالة الكوبون");
    }
  };

  const openHistory = async (couponId) => {
    setHistoryCouponId(couponId);
    setLoadingHistory(true);
    try {
      const rows = await fetchCouponRedemptions(couponId);
      setRedemptions(rows);
    } catch {
      toast.error("فشل تحميل سجل الاستخدام");
      setRedemptions([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const FormFields = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium mb-1">كود الكوبون *</label>
        <input
          className="w-full p-2 border rounded-lg uppercase"
          value={formData.code}
          onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">نوع الخصم *</label>
        <select
          className="w-full p-2 border rounded-lg"
          value={formData.discountType}
          onChange={(e) => setFormData({ ...formData, discountType: e.target.value })}
        >
          <option value="Percentage">نسبة مئوية (%)</option>
          <option value="FixedAmount">مبلغ ثابت (EGP)</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">قيمة الخصم *</label>
        <input
          type="number"
          min="0.01"
          step="0.01"
          className="w-full p-2 border rounded-lg"
          value={formData.discountValue}
          onChange={(e) => setFormData({ ...formData, discountValue: e.target.value })}
          required
        />
      </div>
      <div className="flex items-center gap-2 pt-6">
        <input
          type="checkbox"
          id="isActive"
          checked={formData.isActive}
          onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
        />
        <label htmlFor="isActive">نشط</label>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">تاريخ البداية (اختياري)</label>
        <input
          type="datetime-local"
          className="w-full p-2 border rounded-lg"
          value={formData.startDateUtc}
          onChange={(e) => setFormData({ ...formData, startDateUtc: e.target.value })}
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">تاريخ الانتهاء (اختياري)</label>
        <input
          type="datetime-local"
          className="w-full p-2 border rounded-lg"
          value={formData.expiryDateUtc}
          onChange={(e) => setFormData({ ...formData, expiryDateUtc: e.target.value })}
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">الحد الأقصى للاستخدام (اختياري)</label>
        <input
          type="number"
          min="1"
          className="w-full p-2 border rounded-lg"
          value={formData.maxUses}
          onChange={(e) => setFormData({ ...formData, maxUses: e.target.value })}
          placeholder="غير محدود"
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">الحد الأدنى لكمية المنتجات (اختياري)</label>
        <input
          type="number"
          min="1"
          className="w-full p-2 border rounded-lg"
          value={formData.minimumProductQuantity}
          onChange={(e) => setFormData({ ...formData, minimumProductQuantity: e.target.value })}
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">الحد الأدنى لمجموع المنتجات EGP (اختياري)</label>
        <input
          type="number"
          min="0"
          step="0.01"
          className="w-full p-2 border rounded-lg"
          value={formData.minimumProductsSubtotal}
          onChange={(e) => setFormData({ ...formData, minimumProductsSubtotal: e.target.value })}
        />
      </div>
      <div className="flex items-center gap-2 pt-2">
        <input
          type="checkbox"
          id="oneTimePerCustomer"
          checked={formData.oneTimePerCustomer}
          onChange={(e) => setFormData({ ...formData, oneTimePerCustomer: e.target.checked })}
        />
        <label htmlFor="oneTimePerCustomer">مرة واحدة لكل عميل</label>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 md:p-8" dir="rtl">
      <ToastContainer position="top-center" />

      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-8">
          <div className="flex items-center gap-3">
            <Tag className="text-purple-600" size={32} />
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              إدارة أكواد الخصم
            </h1>
          </div>
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 bg-white/80 backdrop-blur-sm text-gray-700 px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all border border-gray-200"
          >
            <ChevronRight size={20} />
            العودة للرئيسية
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-2xl mb-6 flex items-center gap-3">
            <AlertCircle className="text-red-500" size={24} />
            <p>فشل في تحميل الكوبونات.</p>
          </div>
        )}

        <div className="flex justify-end mb-6">
          <button
            onClick={openCreate}
            className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-xl shadow-md hover:shadow-xl flex items-center gap-2 font-bold"
          >
            <Plus size={20} />
            إنشاء كوبون جديد
          </button>
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 overflow-x-auto">
          {isLoading ? (
            <p className="text-center py-8 text-gray-500">جاري التحميل...</p>
          ) : coupons.length === 0 ? (
            <p className="text-center py-8 text-gray-500">لا توجد كوبونات بعد.</p>
          ) : (
            <table className="w-full min-w-[1000px] table-auto">
              <thead>
                <tr className="bg-gradient-to-r from-purple-50 to-pink-50 border-b-2 border-purple-200">
                  <th className="p-3 text-right font-bold text-purple-800">الكود</th>
                  <th className="p-3 text-right font-bold text-purple-800">نوع الخصم</th>
                  <th className="p-3 text-right font-bold text-purple-800">القيمة</th>
                  <th className="p-3 text-right font-bold text-purple-800">الحالة</th>
                  <th className="p-3 text-right font-bold text-purple-800">الاستخدام</th>
                  <th className="p-3 text-right font-bold text-purple-800">البداية</th>
                  <th className="p-3 text-right font-bold text-purple-800">الانتهاء</th>
                  <th className="p-3 text-center font-bold text-purple-800">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {coupons.map((coupon) => (
                  <tr key={coupon.couponId} className="border-b hover:bg-purple-50/30">
                    <td className="p-3 font-mono font-bold">{coupon.code}</td>
                    <td className="p-3">
                      {coupon.discountType === "Percentage" ? "نسبة %" : "مبلغ ثابت"}
                    </td>
                    <td className="p-3">
                      {coupon.discountType === "Percentage"
                        ? `${coupon.discountValue}%`
                        : `${coupon.discountValue} EGP`}
                    </td>
                    <td className="p-3">
                      <span
                        className={`px-2 py-1 rounded-full text-sm ${
                          coupon.isActive
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-200 text-gray-600"
                        }`}
                      >
                        {coupon.isActive ? "نشط" : "غير نشط"}
                      </span>
                    </td>
                    <td className="p-3 text-sm">
                      {coupon.currentUses}
                      {coupon.isUnlimited
                        ? " / ∞"
                        : ` / ${coupon.maxUses} (${coupon.remainingUses} متبقي)`}
                    </td>
                    <td className="p-3 text-sm">{formatDate(coupon.startDateUtc)}</td>
                    <td className="p-3 text-sm">{formatDate(coupon.expiryDateUtc)}</td>
                    <td className="p-3">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => openEdit(coupon)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="تعديل"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleToggle(coupon.couponId)}
                          className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg"
                          title={coupon.isActive ? "إيقاف" : "تفعيل"}
                        >
                          {coupon.isActive ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                        </button>
                        <button
                          onClick={() => openHistory(coupon.couponId)}
                          className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg"
                          title="سجل الاستخدام"
                        >
                          <History size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {formMode && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {formMode === "create" ? "إنشاء كوبون" : "تعديل كوبون"}
              </h2>
              <button onClick={closeForm} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <FormFields />
              <div className="flex gap-3 mt-6">
                <button
                  type="submit"
                  className="flex-1 bg-purple-600 text-white py-3 rounded-xl flex items-center justify-center gap-2 hover:bg-purple-700"
                >
                  <Save size={18} />
                  حفظ
                </button>
                <button
                  type="button"
                  onClick={closeForm}
                  className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-xl hover:bg-gray-300"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {historyCouponId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">سجل استخدام الكوبون</h2>
              <button
                onClick={() => {
                  setHistoryCouponId(null);
                  setRedemptions([]);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
            {loadingHistory ? (
              <p className="text-center py-6 text-gray-500">جاري التحميل...</p>
            ) : redemptions.length === 0 ? (
              <p className="text-center py-6 text-gray-500">لا يوجد استخدام بعد.</p>
            ) : (
              <table className="w-full table-auto">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="p-2 text-right">رقم الطلب</th>
                    <th className="p-2 text-right">الهاتف</th>
                    <th className="p-2 text-right">الخصم</th>
                    <th className="p-2 text-right">التاريخ</th>
                  </tr>
                </thead>
                <tbody>
                  {redemptions.map((r) => (
                    <tr key={r.couponRedemptionId} className="border-b">
                      <td className="p-2">{r.orderId}</td>
                      <td className="p-2 font-mono text-sm">{r.customerPhoneNormalized}</td>
                      <td className="p-2">{r.discountAmountApplied} EGP</td>
                      <td className="p-2 text-sm">{formatDate(r.usedAtUtc)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CouponsPage;
